import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import Modal from '../../Components/Modal';
import { useRefresh } from '../../Hooks/useRefresh';
import { TextInput } from '../../Components/Inputs/TextInput';
import { Button } from '../../Components/Buttons';
import { DocumentDuplicateIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Checkbox from '../../Components/Inputs/Checkbox';
import { generateRandomHexString } from '../../Utils';
import Radio from '../../Components/Inputs/Radio';
import Select from '../../Components/Inputs/Select';
import Badge from '../../Components/Badge';

export const getTagEventName = (entryEventName, tags, translate) => {
    const isTagEvent = entryEventName?.startsWith("hoodle_tag_");
    const tagEventId = isTagEvent ? parseInt(entryEventName.split("_")[2]) : null;
    const tagEventName = isTagEvent ? tags?.find((tag) => tag.id === tagEventId)?.name || '' : null;
    return isTagEvent ? translate('automatisations.tag_event_name').replace('%1$s', tagEventName) : translate('automatisations.event_trigger') + ' - ' + entryEventName;
}

export const useAutomatisationMappingForTable = (automatisations, tags) => {
    const { formatDate } = useDateFormat();
    const { translate } = useTranslation();
    return automatisations.map((automatisation) => {
        const entryEventNameFormatted = getTagEventName(automatisation.entry_event_name, tags, translate);
        return {
            ...automatisation, 
            key: automatisation.id, 
            created_at: formatDate(automatisation.created_at), 
            publish_date: formatDate(automatisation.publish_date), 
            status: automatisation.is_active ? translate('automatisations.published') : translate('automatisations.draft'),
            is_campaign: automatisation.is_campaign,
            entry_event_name_formatted: entryEventNameFormatted
        };
    });
}

export default function Automatisations() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const [open, setOpen] = useState(false);
    const [selectedAutomatisation, setSelectedAutomatisation] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const api = useAPI();
    const appState = useAppState();
    const { sidebarSearch } = appState;

    const [automatisations, setAutomatisations] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const pagination = usePaginations();
    const { refreshId, refresh } = useRefresh();

    const [tags, setTags] = useState([]);
    const [tagsForFilter, setInternalTagsForFilter] = useState(localStorage.getItem('automatisations_tags_filter') ? JSON.parse(localStorage.getItem('automatisations_tags_filter')) : []);
    const [searchParams] = useSearchParams();
    const is_ghost = searchParams.get('ghost_mode') === 'on';

    const setTagsForFilter = (tags) => {
        setInternalTagsForFilter(tags);
        localStorage.setItem('automatisations_tags_filter', JSON.stringify(tags));
    }

    useEffect(() => {
        
        api.listTags(1, 99999).then((response) => {
            setTags(response.data.tags);
        });
        
    }, [open]);

    const handleSort = ({ key }) => {
        if (pagination.sortBy === key) {
            pagination.setSortOrder(pagination.sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            pagination.setSortBy(key);
            pagination.setSortOrder('asc');
        }
        pagination.setPage(1);
    }

    useEffect(() => {
        pagination.setPage(1);
    }, [sidebarSearch]);

    useEffect(() => {
        const fetchAutomatisations = async () => {
            try {
                setLoading(true);
                const response = await api.listPaginatedAutomatisations(pagination.page, pagination.pageSize, is_ghost, tagsForFilter.map((tag) => tag.id), sidebarSearch, pagination.sortBy, pagination.sortOrder);
                setAutomatisations(response.data.automatisations);
                pagination.setMetaData(response.data.meta);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchAutomatisations();
    }, [pagination.page, pagination.sortBy, pagination.sortOrder, refreshId, sidebarSearch, tagsForFilter]);

    const handleDelete = async (automatisation) => {
        try {
            await api.deleteAutomatisation(automatisation.id);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const handleCopy = async (automatisation) => {
        try {
            await api.copyAutomatisation(automatisation.id);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const mappedAutomatisations = useAutomatisationMappingForTable(automatisations, tags);
    return (
        <>
            {/* Place the Modal outside the main container */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={selectedAutomatisation ? translate('automatisations.edit') : translate('automatisations.create')}
                description={''}
                children={<AutomatisationModal refresh={refresh} setOpen={setOpen} automatisation={selectedAutomatisation} setSelectedAutomatisation={setSelectedAutomatisation} open={open} tags={tags} />}
            />

            <Modal
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                title={translate('automatisations.filter')}
                description={''}
                children={<AutomatisationFilterModal setTagsForFilter={setTagsForFilter} tagsForFilter={tagsForFilter} tags={tags} refresh={refresh} setOpen={setFilterOpen} open={filterOpen} />}
            />

            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="automatisations">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                        <LoadingCircleTransparent />
                    </div> : (
                        <Table
                            title={translate('automatisations.title')}
                            description={translate('automatisations.description')}
                            data={mappedAutomatisations}
                            translationKey="automatisations.table"
                            columns={[{key: 'name', type: 'custom', render: (row) => <AutomatisationName row={row} />}, 'entry_event_name_formatted', { key: 'status', type: 'badge', color: (row) => row.status === translate('automatisations.published') ? 'green' : 'gray' }, 'publish_date', 'created_at']}
                            pagination={pagination}
                            onSort={handleSort}
                            onRowClick={(row) => {
                                navigate(`/automatisation/${row.id}`);
                            }}
                            createButton={{
                                text: translate('automatisations.create'),
                                action: () => {
                                    setSelectedAutomatisation(null);
                                    setOpen(true);
                                }
                            }}
                            filterButton={{
                                text: `${translate('automatisations.filter')} (${tagsForFilter.length})`,
                                action: () => {
                                    setFilterOpen(true);
                                }
                            }}
                            dropdownGroups={[{
                                options: [{
                                    label: translate('edit'),
                                    icon: <PencilIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => { setSelectedAutomatisation(row); setOpen(true) }
                                }
                                ]
                            }, {
                                options: [{
                                    label: translate('clone'),
                                    icon: <DocumentDuplicateIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => handleCopy(row)
                                }]
                            }, {
                                options: [{
                                    label: translate('delete'),
                                    icon: <TrashIcon aria-hidden="true" className="mr-3 size-5 text-red-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => handleDelete(row)
                                }]
                            }]}
                        />
                    )}
                </SidebarNav>
            </div>
        </>

    );
}

export const AutomatisationName = ({ row }) => {
    return <div className="flex flex-row space-x-2">
        <span>{row.name}</span>
        {row.tags && row.tags.length > 0 && row.tags.map((tag) => <Badge key={tag.id} text={tag.name} color={tag.color} withoutRemove={true} />)}
    </div>
}

export const AutomatisationFilterModal = ({ refresh, setOpen, open, setTagsForFilter, tagsForFilter = [], tags = [] }) => {
    const { translate } = useTranslation();

    const addTagToFilter = (tag) => {
        if (tagsForFilter.find((t) => t.id === tag.id)) {
            return;
        }

        setTagsForFilter([...tagsForFilter, tag]);
    }

    const removeTagFromFilter = (tag) => {
        setTagsForFilter(tagsForFilter.filter((t) => t.id !== tag.id));
    }
    return <div>
        <div className="flex flex-col mt-4">
            <p className="block text-sm/6 font-medium text-gray-900">{translate('automatisations.tags')}</p>
            <Select options={tags} selected={{label: translate('automatisations.add_tag')}} setSelected={addTagToFilter} />

            <div className="flex flex-row gap-2 flex-wrap mt-2">
                {tagsForFilter.map((tag) => <Badge key={tag.id} text={tag.name} color={tag.color} onClickRemove={() => removeTagFromFilter(tag)} />)}
            </div>
        </div>
        <div className="flex justify-end mt-4 flex-row gap-2">
            <Button className="bg-white border border-red-400 text-red-400" onClick={() => {
                setTagsForFilter([]);
                refresh();
                setOpen(false);
            }}>{translate('clear')}</Button>
            <Button onClick={() => {
                refresh();
                setOpen(false);
            }}>{translate('filter')}</Button>
        </div>
    </div>
}

export const AutomatisationModal = ({ refresh, setOpen, automatisation, setSelectedAutomatisation, open, isCampaign = false, tags }) => {

    const [name, setName] = useState(automatisation ? automatisation.name : '');
    const [entryEventName, setEntryEventName] = useState(automatisation ? automatisation.entry_event_name : '');
    const [isActive, setIsActive] = useState(automatisation ? automatisation.is_active : false);
    const [automatisationTags, setAutomatisationTags] = useState(automatisation ? automatisation.tags : []);
    const { translate } = useTranslation();
    const api = useAPI();
    const navigate = useNavigate();

    const handleCreate = async () => {
        try {
            const response = await api.createAutomatisation({
                name,
                entry_event_name: isCampaign ? generateRandomHexString() : entryEventName,
                is_active: !isCampaign ? isActive : true,
                is_campaign: isCampaign,
                tags: automatisationTags
            });

            console.log(response);

            if (!isCampaign) {
                navigate(`/automatisation/${response.data.automatisation.id}`);
            } else {
                navigate(`/campaign/${response.data.automatisation.id}`);
            }

            refresh();

            setOpen(false);
            setName('');
        } catch (error) {
            console.error(error);
        }
    }

    const handleUpdate = async () => {
        try {
            await api.updateAutomatisation({
                ...automatisation,
                name,
                entry_event_name: entryEventName,
                is_active: !isCampaign ? isActive : true,
                is_campaign: isCampaign,
                tags: automatisationTags
            });
            refresh();
            setOpen(false);
        } catch (error) {
            console.error(error);
        }
    }


    const entryEventOptions = [
        { id: 'custom', name: translate('automatisations.custom') },
        { id: 'tag', name: translate('automatisations.tag') },
    ]

    const selectedEntryEvent = entryEventName?.startsWith("hoodle_tag_") ? entryEventOptions[1] : entryEventOptions[0];

    const setEntryEventType = (entryEvent) => {
        if (entryEvent === 'tag') {
            setEntryEventName("hoodle_tag_");
        } else {
            setEntryEventName('');
        }
    }

    const selectTag = (tag) => {
        setEntryEventName("hoodle_tag_" + tag.id + "_added");
    }

    const tagSelected = tags?.length > 0 ? tags.find((tag) => tag.id === parseInt(entryEventName.split("_")[2])) : null;

    const buttonDisabled = !name || name.length === 0 || (!isCampaign && (!entryEventName || entryEventName.length === 0));

    const addTag = (tag) => {
        if (automatisationTags.find((t) => t.id === tag.id)) {
            return;
        }

        setAutomatisationTags([...automatisationTags, tag]);
    }

    const removeTag = (tag) => {
        setAutomatisationTags(automatisationTags.filter((t) => t.id !== tag.id));
    }

    return (
        <div className="mt-4 overflow-visible">
            <TextInput id="name" name="name" label={translate('automatisations.name')} placeholder={translate('automatisations.name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} />
            {!isCampaign && <div className="flex flex-col mt-4">
                <p className="block text-sm/6 font-medium text-gray-900">{translate('automatisations.trigger')}</p>
                <Radio options={entryEventOptions} selected={selectedEntryEvent} onChange={setEntryEventType} />
                <div className="mt-2">
                    {selectedEntryEvent.id === 'custom' && <TextInput id="entry_event_name" name="entry_event_name" label={translate('automatisations.entry_event_name')} placeholder={translate('automatisations.entry_event_name_placeholder')} value={entryEventName} onChange={(e) => setEntryEventName(e.target.value)} />}
                    {selectedEntryEvent.id === 'tag' && <Select options={tags} selected={tagSelected} setSelected={selectTag} />}
                </div>
            </div>}
            <div className="flex flex-col mt-4">
                <p className="block text-sm/6 font-medium text-gray-900">{translate('automatisations.tags')}</p>
                <Select options={tags} selected={{label: translate('automatisations.add_tag')}} setSelected={addTag} />

                <div className="flex flex-row gap-2 flex-wrap mt-2">
                    {automatisationTags.map((tag) => <Badge key={tag.id} text={tag.name} color={tag.color} onClickRemove={() => removeTag(tag)} />)}
                </div>
                
            </div>
            {!isCampaign && <div className="flex flex-col mt-4">
                <Checkbox id="is_active" name="is_active" label={translate('automatisations.is_active')} checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </div>}
            <div className="flex justify-end mt-4">
                <Button disabled={buttonDisabled} onClick={automatisation ? handleUpdate : handleCreate}>{automatisation ? translate('update') : translate('create')}</Button>
            </div>
        </div>
    )
}